import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Badge, Button, MetricCard, PageHeader, PageState, Skeleton, StatusDot, cn } from "@fox1ck-org/ui";

import { healthChecks, problemsFirst, useNetworkName, type Check } from "@/components/health/issues";
import { useT } from "@/lib/i18n";
import { formatInt } from "@/lib/format";
import { formatStamp } from "@/lib/utils";
import { useDataHealth } from "@/lib/services/data-health-service";
import { useReportTimezone } from "@/lib/services/workspace-service";

/**
 * «Здоровье данных» (доска Health): can the numbers be trusted right now?
 * Thirteen checks over one GetDataHealth answer — the same facts the alerts
 * read: how many break the numbers, how many may, how many are in order,
 * when the server last looked; then what to sort out, most expensive first,
 * with what each breaks and where to fix it; then the checks in order.
 *
 * Not here, by decision: a «Сверка с Keitaro». The tracker holds no Keitaro
 * data (it is a product of its own).
 */
export default function HealthPage() {
  const t = useT();
  const tz = useReportTimezone();
  const q = useDataHealth();
  const netName = useNetworkName();
  const [folded, setFolded] = useState(false);
  const h = q.data;
  const checks = h ? healthChecks(h, t, netName) : [];
  const problems = problemsFirst(checks);
  const fine = checks.filter((c) => c.tone === "ok");
  const danger = checks.filter((c) => c.tone === "danger").length;
  const warn = checks.filter((c) => c.tone === "warn").length;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
      <PageHeader
        title={t("health.title")}
        subtitle={t("ops.health.subtitle")}
        actions={
          <Button variant="secondary" onClick={() => void q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={cn("size-4", q.isFetching && "animate-spin")} strokeWidth={1.5} />
            {t("ops.health.checkNow")}
          </Button>
        }
      />
      {q.isError && !h ? (
        <PageState mode="error" title={t("health.error")} error={q.error} retry={() => void q.refetch()} retryLabel={t("lib.common.retry")} />
      ) : !h ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3" aria-busy="true">
          <Skeleton className="h-22 w-full shrink-0 rounded-lg" />
          <Skeleton className="h-55 w-full shrink-0 rounded-lg" />
          <Skeleton className="h-42.5 w-full shrink-0 rounded-lg" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <div className="grid shrink-0 grid-cols-4 overflow-hidden rounded-lg border border-border bg-card" role="group" aria-label={t("health.title")}>
            <Tile label={t("ops.health.k.problems")} value={formatInt(danger)} note={t("ops.health.k.problems.note")} tone={danger > 0 ? "danger" : "neutral"} />
            <Tile label={t("ops.health.k.warnings")} value={formatInt(warn)} note={t("ops.health.k.warnings.note")} tone={warn > 0 ? "warn" : "neutral"} />
            <Tile label={t("ops.health.k.ok")} value={formatInt(fine.length)} note={t("ops.health.k.ok.note", { total: formatInt(checks.length) })} tone="ok" />
            <Tile label={t("ops.health.k.last")} value={formatStamp(h.generatedAt, tz, { clock: true }).slice(-8, -3)} note={t("ops.health.k.last.note")} />
          </div>

          {problems.length > 0 ? (
            <section className="shrink-0 overflow-hidden rounded-lg border border-border bg-card" aria-label={t("ops.health.sortOut")}>
              <header className="flex h-11 items-center gap-2 border-b border-border px-3.5">
                <h2 className="text-section-title">{t("ops.health.sortOut")}</h2>
                <Badge variant={danger > 0 ? "danger" : "warn"}>{formatInt(problems.length)}</Badge>
                <span className="ml-auto text-xs text-muted-foreground">{t("ops.health.sortOut.note")}</span>
              </header>
              {problems.map((c) => (
                <ProblemRow key={c.id} c={c} />
              ))}
            </section>
          ) : null}

          <section className="shrink-0 overflow-hidden rounded-lg border border-border bg-card" aria-label={t("ops.health.fine")}>
            <header className="flex h-11 items-center gap-2 border-b border-border px-3.5">
              <h2 className="text-section-title">{t("ops.health.fine")}</h2>
              <Badge variant="ok">{formatInt(fine.length)}</Badge>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setFolded((f) => !f)} aria-expanded={!folded}>
                {folded ? t("ops.health.unfold") : t("ops.health.fold")}
              </Button>
            </header>
            {folded ? null : (
              <div className="grid grid-cols-3">
                {fine.map((c) => (
                  <div key={c.id} className="flex items-start gap-2.5 border-b border-r border-border px-3.5 py-2.5 [&:nth-child(3n)]:border-r-0">
                    <StatusDot tone="ok" className="mt-1.5" />
                    <div className="flex min-w-0 flex-col">
                      <span className="text-sm">{c.title}</span>
                      <span className="truncate text-xs text-muted-foreground">{c.sub}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, note, tone }: { label: string; value: string; note: string; tone?: "ok" | "warn" | "danger" | "neutral" }) {
  return (
    <MetricCard
      labelCase="none"
      label={label}
      value={value}
      comparison={note}
      tone={tone}
      className="min-w-0 rounded-none border-0 border-l border-border px-3.5 py-2.5 first:border-l-0"
    />
  );
}

/** One check that is not in order: what, how much and since when, what it breaks, the way to fix it. */
function ProblemRow({ c }: { c: Check }) {
  const t = useT();
  return (
    <div
      className={cn(
        "grid grid-cols-[--spacing(60)_minmax(0,1fr)_auto] items-center gap-4 border-b border-l-2 border-border px-3.5 py-3 last:border-b-0",
        c.tone === "danger" ? "border-l-destructive" : "border-l-warn",
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-medium">{c.title}</span>
        {c.badge ? (
          <Badge variant={c.tone === "danger" ? "danger" : "warn"} className="w-fit">
            {c.badge}
          </Badge>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs text-muted-foreground">{t("ops.health.breaks")}</span>
        <span className="text-sm">{c.breaks ?? c.sub}</span>
      </div>
      {c.action ? (
        <Button asChild variant={c.tone === "danger" ? "primary" : "secondary"} size="sm">
          <Link to={c.action.to}>{c.action.label}</Link>
        </Button>
      ) : (
        <span />
      )}
    </div>
  );
}
